import React from 'react';
import { Layers } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FieldSplitPanel } from './FieldSplitPanel';
import { LibraryVisualCard } from './LibraryVisualCard';

/**
 * Left sidebar with the Fields / Library tab switcher and search box.
 * All state is owned by the parent builder.
 */
export default function LeftFieldPanel({
  layoutPreset,
  leftPanelTab, setLeftPanelTab,
  fieldSearch, setFieldSearch,
  // Fields tab
  filteredDims, filteredMsrs, filteredCalcMsrs,
  handleBrowseField, handleAddFieldToVisual, handleViewField, handleBinField,
  // Library tab
  libraryVisuals, addLibraryVisual, allFields, modelId, formatValue,
  panelWidth,
}) {
  const useCustomWidth = panelWidth && layoutPreset !== 'minimal' && layoutPreset !== 'stacked';
  return (
    <div
      className={'border-r border-border/50 flex flex-col overflow-hidden ' + (layoutPreset === 'minimal' ? 'hidden' : layoutPreset === 'stacked' ? 'w-full max-h-[30%] border-b border-r-0' : (useCustomWidth ? 'shrink-0' : 'w-56'))}
      style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', order: layoutPreset === 'fields_right' ? 3 : 1, ...(useCustomWidth ? { width: panelWidth } : {}) }}
    >
      <div className="p-3 border-b border-border/50">
        <div className="flex gap-1 mb-2">
          <button onClick={() => setLeftPanelTab('fields')} className={`flex-1 text-[9px] py-1 rounded-md transition-colors ${leftPanelTab === 'fields' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/20'}`} data-testid="left-tab-fields">Fields</button>
          <button onClick={() => setLeftPanelTab('library')} className={`flex-1 text-[9px] py-1 rounded-md transition-colors ${leftPanelTab === 'library' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/20'}`} data-testid="left-tab-library">Library</button>
        </div>
        <Input
          value={fieldSearch}
          onChange={e => setFieldSearch(e.target.value)}
          placeholder={leftPanelTab === 'fields' ? 'Search fields...' : 'Search visuals...'}
          className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30"
          data-testid="field-search"
        />
      </div>
      {leftPanelTab === 'fields' && (
        <FieldSplitPanel
          filteredDims={filteredDims} filteredMsrs={filteredMsrs} filteredCalcMsrs={filteredCalcMsrs}
          handleBrowseField={handleBrowseField} handleAddFieldToVisual={handleAddFieldToVisual} handleViewField={handleViewField} handleBinField={handleBinField}
          highlight={fieldSearch} modelId={modelId}
        />
      )}
      {leftPanelTab === 'library' && (
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1.5">
            {libraryVisuals.length === 0 ? (
              <div className="text-center py-8">
                <Layers className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
                <p className="text-[10px] text-muted-foreground">No library visuals</p>
                <p className="text-[9px] text-muted-foreground/70 mt-0.5">Create visuals in the Visual Library</p>
              </div>
            ) : (
              libraryVisuals
                .filter(lv => !fieldSearch || lv.name?.toLowerCase().includes(fieldSearch.toLowerCase()))
                .map(lv => (
                  <LibraryVisualCard
                    key={lv.visual_id}
                    lv={lv}
                    onAdd={() => addLibraryVisual(lv)}
                    allFields={allFields}
                    modelId={modelId}
                    formatValue={formatValue}
                  />
                ))
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
